import { useState, useCallback } from 'react';
import { recordPublicBookingClick } from '@/api/bookingClicks';
import { isStay22Enabled } from '@/config/bookingExperience';
import {
  buildStay22AllezUrl,
  type Stay22AllezProvider,
} from '@/lib/booking/buildStay22AllezUrl';
import { openStay22InNewTab } from '@/lib/booking/openStay22InNewTab';
import {
  MISSING_PLACE_ERROR,
  SERVICE_UNAVAILABLE_ERROR,
  type BookingErrorCopy,
} from '@/lib/booking/bookingErrors';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_STAY22_OUTBOUND_CLICK,
  EVENT_STAY22_OUTBOUND_POPUP_BLOCKED,
} from '@/lib/analyticsEvents';
import {
  buildBookingCampaignRef,
  resolveBookingCampaignRefContext,
  type BookingCampaignSurface,
} from '@/utils/booking/buildBookingCampaignRef';
import type { Stay22OutboundAnalyticsProps } from '@/lib/analyticsAttribution';
import { getReferralCreatorIdForApi } from '@/lib/analyticsAttribution';

export interface OpenStay22DeepLinkParams {
  userId?: string;
  postId?: string;
  placeId?: string;
  googlePlaceId?: string;
  surface: BookingCampaignSurface;
  locationName?: string;
  contentType?: 'itinerary' | 'list';
  address?: string;
  lat?: number;
  lng?: number;
  checkIn?: string;
  checkOut?: string;
  adults?: number;
  children?: number;
  /** Allez endpoint segment; defaults to `roam` when omitted. */
  provider?: Stay22AllezProvider;
  /** When true, caller already fired Stay22 Outbound Click (e.g. before auth gate). */
  skipAnalytics?: boolean;
}

function buildStay22OutboundAnalyticsProps(
  params: OpenStay22DeepLinkParams,
): Stay22OutboundAnalyticsProps {
  const { refType, refId } = resolveBookingCampaignRefContext({
    postId: params.postId,
    placeId: params.placeId,
  });

  const cmp = buildBookingCampaignRef({
    userId: params.userId,
    refType,
    refId,
    surface: params.surface,
    vendor: 'stay22',
  });

  const referralCreatorId = getReferralCreatorIdForApi();

  return {
    surface: params.surface,
    vendor: 'stay22',
    cmp,
    ...(params.userId && { user_id: params.userId }),
    ...(refType === 'post' && refId && { post_id: refId }),
    ...(refType === 'place' && refId && { place_id: refId }),
    ...(params.googlePlaceId && { google_place_id: params.googlePlaceId }),
    ...(params.locationName && { location_name: params.locationName }),
    ...(params.contentType && { content_type: params.contentType }),
    ...(params.lat != null && { lat: params.lat }),
    ...(params.lng != null && { lng: params.lng }),
    ...(referralCreatorId && { referring_creator_id: referralCreatorId }),
  };
}

/** Fire Stay22 Outbound Click without opening a tab (use before auth gates). */
export function trackStay22OutboundClick(params: OpenStay22DeepLinkParams): void {
  if (!isStay22Enabled()) {
    return;
  }

  WebAnalytics.track(EVENT_STAY22_OUTBOUND_CLICK, buildStay22OutboundAnalyticsProps(params));
}

export function useOpenStay22DeepLink(): {
  openStay22DeepLink: (params: OpenStay22DeepLinkParams) => void;
  isOpening: boolean;
  blockedUrl: string | undefined;
  clearBlockedState: () => void;
  /** Set when the booking site can't be opened; callers show it in BookingErrorModal. */
  bookingError: BookingErrorCopy | null;
  clearBookingError: () => void;
} {
  const [isOpening, setIsOpening] = useState(false);
  const [blockedUrl, setBlockedUrl] = useState<string | undefined>(undefined);
  const [bookingError, setBookingError] = useState<BookingErrorCopy | null>(null);

  const openStay22DeepLink = useCallback((params: OpenStay22DeepLinkParams) => {
    if (!isStay22Enabled()) {
      return;
    }

    const hasCoords = params.lat != null && params.lng != null;
    const hasAddress = !!params.address?.trim();
    if (!hasCoords && !hasAddress) {
      setBookingError(MISSING_PLACE_ERROR);
      return;
    }

    setIsOpening(true);
    setBlockedUrl(undefined);
    setBookingError(null);

    try {
      const analyticsProps = buildStay22OutboundAnalyticsProps(params);
      const url = buildStay22AllezUrl({
        lat: params.lat,
        lng: params.lng,
        address: params.address,
        hotelName: params.locationName,
        provider: params.provider,
        checkIn: params.checkIn,
        checkOut: params.checkOut,
        adults: params.adults,
        children: params.children,
        campaign: analyticsProps.cmp,
      });

      if (!url) {
        setBookingError(SERVICE_UNAVAILABLE_ERROR);
        setIsOpening(false);
        return;
      }

      if (!params.skipAnalytics) {
        WebAnalytics.track(EVENT_STAY22_OUTBOUND_CLICK, {
          ...analyticsProps,
          open_type: 'allez',
          allez_provider: params.provider ?? 'roam',
          has_dates: Boolean(params.checkIn && params.checkOut),
        });
        void recordPublicBookingClick({
          locationId: params.placeId,
          postId: params.postId,
          referralCreatorId: getReferralCreatorIdForApi(),
          sourceSurface: params.surface,
        });
      }

      openStay22InNewTab({
        url,
        onSuccess: () => {
          setIsOpening(false);
        },
        onBlocked: (blocked) => {
          WebAnalytics.track(EVENT_STAY22_OUTBOUND_POPUP_BLOCKED, {
            surface: params.surface,
            vendor: 'stay22',
            has_url: !!blocked,
            open_type: 'allez',
            allez_provider: params.provider ?? 'roam',
          });
          setBlockedUrl(blocked);
          setIsOpening(false);
        },
      });
    } catch {
      setBookingError(SERVICE_UNAVAILABLE_ERROR);
      setIsOpening(false);
    }
  }, []);

  const clearBlockedState = useCallback(() => {
    setBlockedUrl(undefined);
  }, []);

  const clearBookingError = useCallback(() => {
    setBookingError(null);
  }, []);

  return {
    openStay22DeepLink,
    isOpening,
    blockedUrl,
    clearBlockedState,
    bookingError,
    clearBookingError,
  };
}
